import dayjs from 'dayjs';
import React, { FC, MouseEventHandler } from 'react';
import Ripples from 'react-ripples';
import { AddressDeliveryType } from '../../../models/address-delivery-type';
import { AddressOpeningHours } from '../../../models/address-opening-hours';
import { CountryType } from '../../../models/country-type';
import { DeliveryAddressesType } from '../../../models/delivery-addresses-type';
import IcomoonIcon from '../../atoms/icomoon-icon/icomoon-icon';
import isUmbracoNullDate from '../../../helpers/is-umbraco-null-date';
import getTailwindConfig from '../../../helpers/get-tailwind-config';

interface Props extends AddressDeliveryType, Partial<DeliveryAddressesType> {
  readonly countries: CountryType[];
  readonly onDelete?: MouseEventHandler<HTMLButtonElement>;
  readonly onEdit?: MouseEventHandler<HTMLButtonElement>;
}

const DeliveryAddressCard: FC<Props> = ({
  addressLine1,
  addressLine2,
  addressLine3,
  addressLine4,
  addressLine5,
  countries,
  countryCode,
  deleteAddressLinkText,
  editAddressLinkText,
  onDelete,
  onEdit,
  openingHours,
  name,
  postcode,
}: Props) => {
  const fullConfig = getTailwindConfig();
  const frenchBlue = fullConfig.theme.colors['french-blue'];

  return (
    <div className="border border-grey-200 flex flex-col items-center justify-between px-4 py-6 md:p-8 rounded-lg">
      <div>
        {name && <h5 className="page-heading-five mb-3 text-center">{name}</h5>}

        <div className="flex flex-col items-center text-center">
          <span>{addressLine1}</span>
          {addressLine2 && <span>{addressLine2}</span>}
          {addressLine3 && <span>{addressLine3}</span>}
          {addressLine4 && <span>{addressLine4}</span>}
          {addressLine5 && <span>{addressLine5}</span>}
          {postcode && <span>{postcode}</span>}
          <span>
            {
              countries?.find(
                (country: CountryType) => country.iso3Code === countryCode
              )?.name
            }
          </span>
        </div>

        {openingHours?.length && (
          <div className="flex flex-col items-center mt-4 text-center text-grey-600">
            <span>Opening Hours:</span>
            {openingHours.map((openingHour: AddressOpeningHours) => {
              const timesAreDifferent =
                dayjs(openingHour.startTime).format('HH:mm') !==
                dayjs(openingHour.endTime).format('HH:mm');
              const dateIsNotNull =
                !isUmbracoNullDate(
                  openingHour.startTime as unknown as string
                ) &&
                !isUmbracoNullDate(openingHour.endTime as unknown as string);
              const isOpen = timesAreDifferent && dateIsNotNull;

              return (
                <div key={openingHour.period}>
                  <span>{openingHour.period}:</span>
                  <span>&nbsp;</span>

                  {isOpen && (
                    <>
                      <span>
                        {dayjs(openingHour.startTime).format('HH:mm')}
                      </span>
                      <span>&nbsp;-&nbsp;</span>
                      <span>{dayjs(openingHour.endTime).format('HH:mm')}</span>
                    </>
                  )}

                  {!isOpen && <span>Closed</span>}
                </div>
              );
            })}
          </div>
        )}
      </div>

      {(onDelete || onEdit) && (
        <div className="flex space-x-4 justify-center mt-4 w-full">
          {onEdit && (
            <Ripples
              className="rounded-full"
              color={`rgba(${frenchBlue.rgb}, .2)`}
              during={1200}
            >
              <button
                className="flex items-center focus:outline-none px-4 py-2 rounded-full text-grey-600 text-left"
                onClick={onEdit}
                type="button"
              >
                <IcomoonIcon color="french-blue" name="edit" size="lg" />
                <span className="ml-4">{editAddressLinkText}</span>
              </button>
            </Ripples>
          )}

          {onDelete && (
            <Ripples
              className="rounded-full"
              color={`rgba(${frenchBlue.rgb}, .2)`}
              during={1200}
            >
              <button
                className="flex items-center focus:outline-none px-4 py-2 rounded-full text-grey-600 text-left"
                onClick={onDelete}
                type="button"
              >
                <IcomoonIcon color="french-blue" name="trash" size="lg" />
                <span className="ml-4">{deleteAddressLinkText}</span>
              </button>
            </Ripples>
          )}
        </div>
      )}
    </div>
  );
};

DeliveryAddressCard.defaultProps = {
  onDelete: null,
  onEdit: null,
};

export default DeliveryAddressCard;
